import { Head, Link, usePage } from '@inertiajs/react'
import { Mountain } from 'lucide-react'
import { SharedProps, HomeProps } from '../../types'
import Layout from '../../components/Layout'
import HeroBanner from '../../components/home/HeroBanner'
import WeeklyScoreboard from '../../components/home/WeeklyScoreboard'
import KeyQuestions from '../../components/home/KeyQuestions'
import QuickActions from '../../components/home/QuickActions'
import RecentMeetings from '../../components/home/RecentMeetings'
import StatNumber from '../../components/shared/StatNumber'

export default function Index(props: Partial<HomeProps>) {
  const { current_user } = usePage<{ props: SharedProps }>().props

  if (!current_user) {
    return (
      <>
        <Head title="Pinnacle - Weekly Tactical Meetings" />
        <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
          <div className="max-w-lg w-full text-center">
            <h1 className="text-5xl font-bold text-text-primary mb-4 font-heading">Pinnacle</h1>
            <p className="text-2xl text-text-secondary mb-2">Weekly Tactical Meeting Platform</p>
            <p className="text-lg text-text-muted mb-8">For Those Who Never Stop Climbing</p>

            <div className="space-y-4">
              <Link
                href="/users/sign_in"
                className="block w-full bg-pinnacle-red hover:bg-pinnacle-red-dark text-white font-semibold py-3.5 px-6 rounded-lg transition-colors font-heading text-lg"
              >
                Sign In
              </Link>
              <Link
                href="/users/sign_up"
                className="block w-full bg-white hover:bg-gray-50 text-text-primary font-semibold py-3.5 px-6 rounded-lg border border-stone/30 transition-colors font-heading text-lg"
              >
                Create Account
              </Link>
            </div>
          </div>
        </div>
      </>
    )
  }

  const {
    user,
    scoreboard = [],
    recent_meetings = [],
    team_id = null,
    team_agenda_name = null,
    weekly_tactical_template = null,
    priorities_summary = { total: 0, count_at_100: 0, average_percent: 0 },
  } = props

  const avgPctLabel = priorities_summary.average_percent % 1 === 0
    ? `${priorities_summary.average_percent}%`
    : `${priorities_summary.average_percent.toFixed(1)}%`

  const firstName = user?.first_name || current_user.first_name

  return (
    <Layout>
      <Head title="Home - Pinnacle" />

      <div className="max-w-7xl mx-auto space-y-8">
        <HeroBanner firstName={firstName} />

        <WeeklyScoreboard scoreboard={scoreboard} teamId={team_id} />

        <section>
          <h2 className="text-base font-bold uppercase tracking-widest text-text-muted mb-4 font-sans">
            PRIORITIES SNAPSHOT
          </h2>
          {priorities_summary.total > 0 ? (
            <div className="flex flex-wrap gap-4">
              <div className="bg-white rounded-xl shadow-card border border-stone/10 p-5 min-w-0" style={{ flex: '1 1 140px', minWidth: 140 }}>
                <StatNumber size="md" className="text-text-primary">{priorities_summary.total}</StatNumber>
                <p className="text-base font-semibold text-text-muted font-sans">Total Rocks</p>
              </div>
              <div className="bg-white rounded-xl shadow-card border border-stone/10 p-5 min-w-0" style={{ flex: '1 1 140px', minWidth: 140 }}>
                <StatNumber size="md" className="text-status-win">{avgPctLabel}</StatNumber>
                <p className="text-base font-semibold text-text-muted font-sans">Avg Complete</p>
              </div>
              <div className="bg-white rounded-xl shadow-card border border-stone/10 p-5 min-w-0" style={{ flex: '1 1 140px', minWidth: 140 }}>
                <StatNumber size="md" className="text-sea">{priorities_summary.count_at_100}</StatNumber>
                <p className="text-base font-semibold text-text-muted font-sans">At 100%</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-card border border-stone/10 p-8 text-center">
              <Mountain className="w-12 h-12 text-text-muted mx-auto mb-3" />
              <p className="text-xl font-semibold text-text-primary mb-2 font-sans">No priorities set yet</p>
              <p className="text-lg text-text-muted mb-4">Define your quarterly rocks to keep your team focused on what matters most.</p>
              {team_id ? (
                <Link
                  href={`/teams/${team_id}/rocks`}
                  className="inline-block bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-base font-sans"
                >
                  Set Up Rocks
                </Link>
              ) : (
                <Link
                  href="/teams"
                  className="inline-block bg-pinnacle-red hover:bg-pinnacle-red/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors text-base font-sans"
                >
                  Join a Team
                </Link>
              )}
            </div>
          )}
        </section>

        <KeyQuestions />

        <QuickActions teamId={team_id} weeklyTacticalTemplate={weekly_tactical_template} />

        <RecentMeetings meetings={recent_meetings} teamId={team_id} agendaName={team_agenda_name} />
      </div>
    </Layout>
  )
}
